import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Award, MapPin, Building, Users, Heart, Shield, Clock } from 'lucide-react';

const AboutSection = () => {
  const { language } = useLanguage();

  const milestones = [
    { year: '1985', text: language === 'ta' ? '20 படுக்கைகள் கொண்ட நர்சிங் ஹோம், மண்ணடி' : '20-bed Nursing Home, Mannady' },
    { year: '2000', text: language === 'ta' ? 'பல்துறை சிறப்பு மருத்துவமனை, திருவல்லிக்கேணி' : 'Multi-Specialty Hospital, Triplicane' },
    { year: '2015', text: language === 'ta' ? 'ஓம்சக்தி மல்டி ஸ்பெஷாலிட்டி கிளை' : 'Omsakthi Multi Speciality branch' },
    { year: '2017', text: language === 'ta' ? 'NABH அங்கீகாரம்' : 'NABH Accreditation' },
  ];

  return (
    <section id="about" className="py-16 lg:py-20 bg-white" data-testid="about-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Compact Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-[#0066b3]/8 text-[#0066b3] rounded-full text-xs font-semibold uppercase tracking-wider mb-3">
            {language === 'ta' ? 'எங்களைப் பற்றி' : 'About Us'}
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f172a] tracking-tight">
            {language === 'ta' ? 'சென்னையின் சிறந்த மருத்துவமனை' : 'Best Multi-Specialty Hospital in Chennai'}
          </h2>
          <p className="text-sm text-[#64748b] mt-2 max-w-xl mx-auto">
            {language === 'ta' ? 'NABH அங்கீகாரம் பெற்ற 75 படுக்கை மருத்துவமனை - திருவல்லிக்கேணி, ராயப்பேட்டை & மயிலாப்பூர்' : 'NABH Accredited 75-bedded hospital serving Triplicane, Royapettah, Mylapore & Central Chennai since 2000'}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {/* Left - Image with overlay */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_health-portal-129/artifacts/7w7ryv52_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2001_52_51%20PM.png"
                alt="Sakthi Hospital doctors and medical staff team - Caring like family, Healing with trust"
                className="w-full h-[340px] object-cover"
              />
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-5 right-6 bg-[#0066b3] text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3">
              <Award size={24} className="text-[#d4a24c]" />
              <div>
                <div className="text-2xl font-extrabold leading-none">25+</div>
                <div className="text-xs opacity-80">{language === 'ta' ? 'ஆண்டுகள்' : 'Years'}</div>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div>
            <div className="flex flex-wrap gap-2 mb-4">
              {['Triplicane', 'Royapettah', 'Mylapore', 'Chepauk', 'Mandaveli'].map((loc) => (
                <span key={loc} className="inline-flex items-center gap-1 px-3 py-1 bg-[#c41e3a]/8 text-[#c41e3a] rounded-full text-xs font-medium">
                  <MapPin size={12} />{loc}
                </span>
              ))}
            </div>

            <p className="text-[#4a5568] leading-relaxed mb-6 text-sm">
              {language === 'ta'
                ? 'சக்தி மருத்துவமனை & ஆராய்ச்சி மையம் சென்னையின் சிறந்த மருத்துவமனைகளில் ஒன்றாகும். NABH அங்கீகாரம் பெற்ற 75 படுக்கைகள் கொண்ட பல்துறை சிறப்பு மருத்துவமனை. 2,00,000+ குடும்பங்கள் நம்புகின்றன.'
                : 'Sakthi Hospital & Research Centre is one of the best hospitals in Chennai, serving patients from Triplicane, Royapettah, Mylapore, Chepauk, Mandaveli and surrounding areas. Our NABH accredited 75-bedded multi-specialty hospital offers world-class treatment at affordable costs, trusted by over 2,00,000 families.'}
            </p>

            {/* Vision & Mission - inline */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="bg-[#0066b3]/5 rounded-lg p-3 border-l-3 border-[#0066b3]">
                <h5 className="text-[11px] font-bold text-[#0066b3] uppercase tracking-wider mb-1">Vision</h5>
                <p className="text-xs text-[#5a6b7c] leading-relaxed">High quality medical care at affordable cost.</p>
              </div>
              <div className="bg-[#c41e3a]/5 rounded-lg p-3 border-l-3 border-[#c41e3a]">
                <h5 className="text-[11px] font-bold text-[#c41e3a] uppercase tracking-wider mb-1">Mission</h5>
                <p className="text-xs text-[#5a6b7c] leading-relaxed">Trust, Honesty, Respect, Equality & Ethics.</p>
              </div>
            </div>

            {/* Key Stats - compact row */}
            <div className="grid grid-cols-4 gap-2 mb-6">
              {[
                { icon: Building, val: '75+', label: language === 'ta' ? 'படுக்கைகள்' : 'Beds' },
                { icon: Shield, val: 'NABH', label: language === 'ta' ? 'அங்கீகாரம்' : 'Accredited' },
                { icon: Users, val: '1.5L+', label: language === 'ta' ? 'குடும்பங்கள்' : 'Families' },
                { icon: Heart, val: '24/7', label: language === 'ta' ? 'அவசரம்' : 'Emergency' },
              ].map((s) => (
                <div key={s.val} className="text-center p-2 bg-[#f8fafc] rounded-lg">
                  <s.icon size={16} className="text-[#0066b3] mx-auto mb-1" />
                  <div className="text-sm font-bold text-[#0f172a]">{s.val}</div>
                  <div className="text-[10px] text-[#64748b]">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Timeline - horizontal compact */}
            <div className="flex items-center gap-0 overflow-x-auto" data-testid="milestones-timeline">
              {milestones.map((m, i) => (
                <div key={m.year} className="flex items-center">
                  <div className="text-center px-3 py-2 min-w-fit">
                    <div className="text-xs font-bold text-[#0066b3]">{m.year}</div>
                    <div className="text-[10px] text-[#64748b] leading-tight mt-0.5">{m.text}</div>
                  </div>
                  {i < milestones.length - 1 && (
                    <div className="w-6 h-[2px] bg-[#0066b3]/20 flex-shrink-0"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
